'use client';

import { setAuthCookieAction } from '@insta-club/action';
import { ApiKeys, fetcher } from '@insta-club/api';
import { ArrowIcon, ChevronIcon, InfoIcon, LockIcon, PenIcon } from '@insta-club/components';
import { useCustomForm, useOtpCountdown } from '@insta-club/hooks';
import { useSessionStore } from '@insta-club/state';
import { Button, Form } from '@insta-club/ui';
import { detectUsernameTypeDetailed } from '@insta-club/utilities';
import { VerificationCodeFormValues, verificationCodeSchema } from '@insta-club/validator';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import OtpInput from 'react-otp-input';

type AuthMethod = 'login' | 'forget-password' | 'register';

function OtpForm() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const exist = searchParams.get('exist');
    const username = searchParams.get('username');
    const method = searchParams.get('method') as AuthMethod as string;

    const { start, isDisabled, formatted } = useOtpCountdown({
        duration: 180,
        storageKey: 'otp-end'
    });

    const sendOTP = useCallback(async () => {
        await fetcher(ApiKeys.SEND_VERIFICATION_CODE, 'web', 'POST', {
            body: {
                username
            }
        });
    }, [username]);

    useEffect(() => {
        if (!isDisabled) {
            sendOTP();
            start();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const form = useCustomForm(verificationCodeSchema, { verification_code: '' });

    useEffect(() => {
        if (!exist || !username) {
            router.push('/auth/sign-in');
        }
    }, [exist, username, router]);

    const onSubmit: SubmitHandler<VerificationCodeFormValues> = async (data) => {
        try {
            const { verification_code } = data;
            let res: {
                message: boolean;
                verification_code: number;
                admin: boolean;
                token: string;
                status: number;
                user: object;
            } | null = null;

            if (method === 'login') {
                res = await fetcher(ApiKeys.LOGIN, 'web', 'POST', {
                    body: {
                        username,
                        verification_code
                    }
                });

                if (!!res?.user) {
                    const cookie = await setAuthCookieAction(res?.token);
                    if (cookie?.success) {
                        useSessionStore.getState().updateSession();
                        return router.push('/dashboard');
                    } else {
                        toast.error(cookie.error ?? 'مشکلی پیش آمده');
                    }
                }
                toast.error('کد تاییدیه اشتباه میباشد');
            }

            if (method === 'register') {
                res = await fetcher(ApiKeys.VALIDATE_VERIFICATION_CODE, 'web', 'POST', {
                    body: {
                        username,
                        verification_code
                    }
                });

                if (!!res?.message && res?.status !== 400) {
                    router.push(`/auth/sign-up?username=${username}&verification_code=${res.verification_code}`);
                    return;
                }

                toast.error('کد تاییدیه اشتباه میباشد');
            }

            if (method === 'forget-password') {
                res = await fetcher(ApiKeys.VALIDATE_VERIFICATION_CODE, 'web', 'POST', {
                    body: {
                        username,
                        verification_code
                    }
                });

                if (!!res?.message && res?.status !== 400) {
                    router.push(
                        `/auth/forgot-password/reset?username=${username}&verification_code=${res.verification_code}`
                    );
                    return;
                }
                toast.error('کد تاییدیه اشتباه میباشد');
            }
        } catch (err: unknown) {
            toast.removeAll();
            if (err instanceof Error) {
                toast.error('خطای ناشناخته رخ داده است');
            } else {
                toast.removeAll();
                form.setError('verification_code', {
                    message: (err as { error: { message: string } })?.error.message || 'خطا در اعتبار سنجی کد تایید'
                });
            }
        }
    };

    const handleResendOTP = async () => {
        await fetcher(ApiKeys.SEND_VERIFICATION_CODE, 'web', 'POST', {
            body: {
                username
            }
        });
        start();
    };

    useEffect(() => {
        const code = form.watch('verification_code');
        if (code.length === 4 && !form.formState.isSubmitting) {
            form.handleSubmit(onSubmit)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.watch('verification_code')]);

    return (
        <>
            <p className='mt-6 mb-2 text-center text-sm font-normal text-gray-500'>
                یک کد تایید به {detectUsernameTypeDetailed(username ?? '').label} شما ارسال شده است
            </p>
            <div className='mb-6 flex items-center justify-center'>
                <span className='ml-2 text-base font-semibold text-gray-600'>{username}</span>
                <Link href={'/auth/sign-in'} className='text-primary-600 flex items-center text-sm font-normal'>
                    <PenIcon className='ml-1' />
                    ویرایش
                </Link>
            </div>
            <Form form={form} onSubmit={onSubmit}>
                <OtpInput
                    {...form.register('verification_code')}
                    onChange={(val) => form.setValue('verification_code', val)}
                    value={form.watch('verification_code')}
                    numInputs={4}
                    shouldAutoFocus
                    inputStyle={{
                        direction: 'ltr',
                        minWidth: '3rem',
                        height: '3rem',
                        fontSize: '1.5rem',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        textAlign: 'center',
                        backgroundColor: '#F9FAFB',
                        borderColor: form.formState.errors && form.formState.errors['verification_code'] ? 'red' : ''
                    }}
                    containerStyle={{
                        direction: 'ltr',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '1rem',
                        marginBottom: '10px'
                    }}
                    renderInput={(props) => <input dir='ltr' {...props} inputMode='numeric' />}
                />
                {form.formState.errors && form.formState.errors['verification_code'] && (
                    <p className='mb-3 text-center text-sm text-red-500'>
                        {form.formState.errors['verification_code']?.message as unknown as string}
                    </p>
                )}
                {isDisabled ? (
                    <p className='mb-6 text-center text-sm font-normal text-gray-500'>ارسال مجدد ( {formatted} )</p>
                ) : (
                    <p
                        onClick={() => handleResendOTP()}
                        className='text-primary-500 mb-6 cursor-pointer text-center text-base font-semibold'
                    >
                        ارسال مجدد
                    </p>
                )}
                <Button
                    loading={form.formState.isSubmitting}
                    type='submit'
                    size='lg'
                    fullWidth
                    className='mb-6'
                    icon={<ArrowIcon className='rotate-180 text-white' />}
                    iconPosition='end'
                >
                    <span className='text-base font-semibold'>مرحله بعد</span>
                </Button>
            </Form>

            {method !== 'register' && (
                <Link href={`/auth/password?username=${username}`}>
                    <div className='mb-6 flex items-center justify-between rounded-2xl bg-gray-100 px-6 py-3.5'>
                        <div className='flex items-center'>
                            <LockIcon />
                            <span className='mr-2.5 text-base font-normal text-gray-600'>ورود از طریق رمز عبور</span>
                        </div>
                        <ChevronIcon className='text-gray-500' />
                    </div>
                </Link>
            )}

            <p className='mb-6 flex text-right text-sm font-normal text-gray-500'>
                <InfoIcon className='ml-2' />
                {detectUsernameTypeDetailed(username ?? '').type === 'phone' ? (
                    <>در صورت عدم دریافت پیامک لطفا قسمت اسپم (هرزنامه) تلفن همراه خود را چک کنید</>
                ) : (
                    <>در صورت عدم دریافت کد تأیید قسمت اسپم ( هرزنامه ) ایمیل خود را چک کنید .</>
                )}
            </p>
        </>
    );
}

export default OtpForm;

